import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import type { LegalTemplate, LegalTemplateVersion } from '@/mocks/superAdminLegalTemplates';
import { AVAILABLE_VARIABLES, getActiveVersion } from '@/mocks/superAdminLegalTemplates';

interface TemplateEditorModalProps {
  template: LegalTemplate | null;
  onClose: () => void;
  onSave: (templateId: number, newVersion: LegalTemplateVersion) => void;
}

export default function TemplateEditorModal({ template, onClose, onSave }: TemplateEditorModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (template) {
      const active = getActiveVersion(template);
      setTitle(`Nueva versión — ${template.typeLabel}`);
      setContent(active.content);
      setNotes('');
    }
  }, [template]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!template) return null;

  const insertVariable = (variable: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = content.slice(0, start);
    const after = content.slice(end);
    setContent(before + variable + after);
    setTimeout(() => {
      ta.focus();
      const newPos = start + variable.length;
      ta.setSelectionRange(newPos, newPos);
    }, 50);
  };

  const handleSaveAsDraft = () => {
    if (!title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Título requerido',
        text: 'La versión necesita un título.',
        confirmButtonColor: '#1a1a1a',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
      });
      return;
    }
    const newVersion: LegalTemplateVersion = {
      versionNumber: template.currentVersion + 1,
      title: title.trim(),
      content,
      status: 'draft',
      createdAt: new Date().toISOString(),
      createdBy: 'Admin Global',
      notes: notes.trim() || undefined,
    };
    onSave(template.id, newVersion);
    Swal.fire({
      icon: 'success',
      title: 'Borrador guardado',
      text: `Versión ${newVersion.versionNumber} creada como borrador. Actívala desde la tarjeta de la plantilla.`,
      timer: 3000,
      showConfirmButton: true,
      confirmButtonColor: '#1a1a1a',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
    });
    onClose();
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[75] flex items-start justify-center p-4 pt-8 pb-8 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="relative w-full max-w-[1000px] bg-background-50 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${template.typeIcon} text-foreground-500 text-base`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-foreground-800 text-xs font-semibold truncate">{template.typeLabel}</p>
              <p className="text-foreground-400 text-[10px]">
                Editando nueva versión — Actual: v{template.currentVersion}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveAsDraft}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-draft-line text-xs"></i>
              Guardar Borrador
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className="block text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
              Título de la versión
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Términos de Servicio — Actualización Julio 2026"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>

          {/* Variables toolbar */}
          <div className="bg-background-100 border border-background-200 rounded-xl p-3">
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">
              Insertar Variable Dinámica
            </p>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  onClick={() => insertVariable(v.key)}
                  title={`Ejemplo: ${v.example}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-background-50 border border-background-200 text-foreground-600 text-[10px] font-mono hover:border-foreground-300 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {v.key}
                  <span className="text-foreground-300 text-[9px]">({v.label})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content editor */}
          <div>
            <label className="block text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
              Contenido del documento
            </label>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors resize-none font-mono leading-relaxed"
              placeholder="Escribí el contenido legal del documento. Usá las variables para contenido dinámico..."
            />
            <p className="text-foreground-300 text-[9px] mt-1">
              {content.length} caracteres · Las variables se ven como {'{{variable}}'} y se reemplazan automáticamente en cada contrato
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
              Notas de versión (opcional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej: Agregada cláusula de horario familiar"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-background-200">
          <p className="text-foreground-300 text-[10px]">
            Nueva versión v{template.currentVersion + 1} · {template.typeLabel}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-xl bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveAsDraft}
              className="px-4 py-1.5 rounded-xl bg-foreground-950 text-background-50 text-[10px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-draft-line text-[10px] mr-1"></i>
              Guardar como Borrador
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}